import { useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  ArrowRight,
  Camera,
  CheckCircle2,
  Lock,
  Phone,
  ShieldCheck,
  Timer,
  Upload,
  X,
} from "lucide-react";

const SERVICES = [
  "House Washing",
  "Driveway Cleaning",
  "Patio & Paver Cleaning",
  "Commercial Exterior",
  "Not sure — recommend it",
];

export default function QuoteForm() {
  const [form, setForm] = useState({
    name: "",
    address: "",
    service: "",
    phone: "",
  });
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: e.target.value }));

  const onFiles = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 3);
    setPhotos(
      files.map((f) => ({
        name: f.name,
        size: (f.size / 1024).toFixed(0) + " KB",
        url: URL.createObjectURL(f),
      }))
    );
  };

  const removePhoto = (idx) => {
    setPhotos((p) => {
      const target = p[idx];
      if (target) {
        try { URL.revokeObjectURL(target.url); } catch (_) {}
      }
      return p.filter((_, i) => i !== idx);
    });
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.address || !form.service || !form.phone) return;
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setDone(true);
    }, 900);
  };

  const reset = () => {
    photos.forEach((p) => {
      try { URL.revokeObjectURL(p.url); } catch (_) {}
    });
    setForm({ name: "", address: "", service: "", phone: "" });
    setPhotos([]);
    setDone(false);
  };

  return (
    <section
      id="quote"
      data-testid="quote-section"
      className="relative bg-[#09090B] py-24 sm:py-32 lg:py-40 overflow-hidden"
    >
      {/* ambient accent line */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#E5D0A1]/30 to-transparent" />

      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          {/* Left: Copy */}
          <div className="lg:col-span-5 lg:sticky lg:top-32">
            <p className="font-mono-small text-[#E5D0A1] mb-6">
              <Timer className="w-3 h-3 inline-block mr-2 -mt-0.5" strokeWidth={1.5} />
              30 · Second · Quote
            </p>
            <h2 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl tracking-[-0.035em] leading-[1.02]">
              A quote so fast,
              <br />
              <span className="font-editorial italic text-[#E5D0A1] font-normal">
                you&apos;ll be done before the coffee&apos;s ready.
              </span>
            </h2>
            <p className="font-body text-white/60 text-lg mt-8 leading-relaxed max-w-md">
              Tell us the property, the surface, and a photo if you&apos;ve got
              one. We&apos;ll text a written estimate inside 30 minutes.
            </p>

            <div className="mt-12 space-y-5">
              {[
                { icon: ShieldCheck, label: "Fixed-price quotes, no surprises" },
                { icon: Phone, label: "Reply by text within 30 minutes" },
                { icon: Lock, label: "Your info is never shared. Ever." },
              ].map(({ icon: Icon, label }) => (
                <div
                  key={label}
                  className="flex items-center gap-4 text-white/70"
                >
                  <Icon
                    className="w-5 h-5 text-[#E5D0A1]"
                    strokeWidth={1.5}
                  />
                  <span className="font-body">{label}</span>
                </div>
              ))}
            </div>

            <div className="mt-12 pt-8 border-t border-white/10">
              <p className="font-mono-small text-white/40 mb-3">
                Prefer to call?
              </p>
              <a
                href="tel:+14805550120"
                data-testid="quote-phone-link"
                className="font-display text-white text-2xl sm:text-3xl tracking-tight u-link"
              >
                (480) 555 — 0120
              </a>
            </div>
          </div>

          {/* Right: Form card */}
          <div className="lg:col-span-7">
            <div className="bg-white text-[#09090B] p-7 sm:p-10 lg:p-14 shadow-[0_30px_80px_rgba(0,0,0,0.5)] relative">
              {!done ? (
                <form onSubmit={submit} data-testid="quote-form">
                  <div className="flex items-center justify-between mb-10">
                    <p className="font-mono-small text-[#09090B]/60">
                      Step · 01 of 01
                    </p>
                    <div className="flex gap-1">
                      <span className="w-8 h-px bg-[#09090B]" />
                      <span className="w-8 h-px bg-[#09090B]/15" />
                      <span className="w-8 h-px bg-[#09090B]/15" />
                    </div>
                  </div>

                  <div className="space-y-7">
                    <div>
                      <label className="font-mono-small text-[#09090B]/60 block mb-1">
                        Full Name
                      </label>
                      <input
                        data-testid="quote-input-name"
                        type="text"
                        required
                        value={form.name}
                        onChange={update("name")}
                        placeholder="Avery Mitchell"
                        className="minimal-input"
                      />
                    </div>

                    <div>
                      <label className="font-mono-small text-[#09090B]/60 block mb-1">
                        Property Address
                      </label>
                      <input
                        data-testid="quote-input-address"
                        type="text"
                        required
                        value={form.address}
                        onChange={update("address")}
                        placeholder="10204 E Camelback Ridge, Scottsdale AZ"
                        className="minimal-input"
                      />
                    </div>

                    <div>
                      <label className="font-mono-small text-[#09090B]/60 block mb-2">
                        Service Needed
                      </label>
                      <Select
                        value={form.service}
                        onValueChange={(v) =>
                          setForm((s) => ({ ...s, service: v }))
                        }
                      >
                        <SelectTrigger
                          data-testid="quote-select-service"
                          className="h-14 border-0 border-b border-black/20 rounded-none bg-transparent px-0 text-base text-[#09090B] focus:ring-0 focus:outline-none shadow-none data-[placeholder]:text-[#09090B]/40"
                        >
                          <SelectValue placeholder="Choose a service…" />
                        </SelectTrigger>
                        <SelectContent className="rounded-none border-black/20 bg-white text-[#09090B]">
                          {SERVICES.map((s) => (
                            <SelectItem
                              key={s}
                              value={s}
                              data-testid={`quote-service-option-${s.replace(/\s|&|—/g, "-").toLowerCase()}`}
                              className="rounded-none focus:bg-[#E5D0A1] focus:text-[#09090B]"
                            >
                              {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="font-mono-small text-[#09090B]/60 block mb-1">
                        Phone
                      </label>
                      <input
                        data-testid="quote-input-phone"
                        type="tel"
                        required
                        value={form.phone}
                        onChange={update("phone")}
                        placeholder="(480) 555 — 0120"
                        className="minimal-input"
                      />
                    </div>

                    {/* Photo upload */}
                    <div>
                      <label className="font-mono-small text-[#09090B]/60 block mb-3">
                        Optional · Photo of the surface
                      </label>
                      <label
                        htmlFor="photo-upload"
                        data-testid="quote-photo-dropzone"
                        className="group flex items-center justify-between gap-4 border border-dashed border-[#09090B]/25 px-5 py-5 hover:border-[#09090B] cursor-pointer transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-[#09090B] text-white flex items-center justify-center">
                            <Camera className="w-4 h-4" strokeWidth={1.5} />
                          </div>
                          <div>
                            <p className="font-medium text-[#09090B]">
                              Drop a photo or tap to upload
                            </p>
                            <p className="text-xs text-[#09090B]/50 mt-1">
                              JPG, PNG or HEIC · up to 3 photos
                            </p>
                          </div>
                        </div>
                        <Upload
                          className="w-5 h-5 text-[#09090B]/40 group-hover:text-[#09090B] transition-colors"
                          strokeWidth={1.5}
                        />
                      </label>
                      <input
                        id="photo-upload"
                        data-testid="quote-input-photo"
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={onFiles}
                        className="hidden"
                      />

                      {photos.length > 0 && (
                        <div className="mt-4 flex flex-wrap gap-3">
                          {photos.map((p, i) => (
                            <div
                              key={i}
                              data-testid={`quote-photo-preview-${i}`}
                              className="relative w-20 h-20 border border-black/15 overflow-hidden group"
                            >
                              <img
                                src={p.url}
                                alt={p.name}
                                className="w-full h-full object-cover"
                              />
                              <button
                                type="button"
                                onClick={() => removePhoto(i)}
                                data-testid={`quote-photo-remove-${i}`}
                                className="absolute top-1 right-1 w-5 h-5 bg-black text-white flex items-center justify-center"
                              >
                                <X className="w-3 h-3" strokeWidth={2} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <button
                    type="submit"
                    data-testid="quote-submit-button"
                    disabled={submitting}
                    className="group mt-10 w-full bg-[#09090B] text-white py-5 text-base font-medium flex items-center justify-center gap-3 hover:bg-[#1c1c1f] transition-colors disabled:opacity-60"
                  >
                    {submitting ? (
                      <>
                        <span className="w-2 h-2 bg-[#E5D0A1] animate-pulse" />
                        Sending…
                      </>
                    ) : (
                      <>
                        Get My Quote
                        <ArrowRight
                          className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-1"
                          strokeWidth={1.5}
                        />
                      </>
                    )}
                  </button>

                  <p className="text-center text-xs text-[#09090B]/40 mt-4 font-body">
                    By submitting, you agree to receive a single follow-up text.
                    No spam, ever.
                  </p>
                </form>
              ) : (
                <div
                  data-testid="quote-success-state"
                  className="py-6 text-center"
                >
                  <div
                    className="mx-auto w-16 h-16 bg-[#09090B] text-[#E5D0A1] flex items-center justify-center mb-8"
                    style={{ animation: "liveSlideIn 0.6s cubic-bezier(0.22, 1, 0.36, 1) both" }}
                  >
                    <CheckCircle2 className="w-7 h-7" strokeWidth={1.2} />
                  </div>
                  <p className="font-mono-small text-[#09090B]/60 mb-4">
                    Quote · Received
                  </p>
                  <h3 className="font-display text-[#09090B] text-3xl sm:text-4xl tracking-tight">
                    Thanks, {form.name.split(" ")[0] || "friend"}.
                  </h3>
                  <p className="font-body text-[#09090B]/60 text-base mt-4 max-w-md mx-auto">
                    We&apos;ll text a written estimate to{" "}
                    <span className="font-medium text-[#09090B]">
                      {form.phone}
                    </span>{" "}
                    within 30 minutes. If it&apos;s after hours, expect a reply
                    by 8am.
                  </p>
                  <button
                    onClick={reset}
                    data-testid="quote-reset-button"
                    className="mt-10 inline-flex items-center gap-2 text-sm font-medium text-[#09090B] u-link"
                  >
                    Submit another request
                    <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </div>
              )}
            </div>

            <p className="font-mono-small text-white/30 mt-6 text-center">
              Avg. response time today · 11 minutes
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
